import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { AnnualHeatmapTable } from '@/components/annual-calendar/AnnualHeatmapTable';
import { AnnualHeatmapLegend } from '@/components/annual-calendar/AnnualHeatmapLegend';
import { SUPPORTED_YEARS, type AnnualYearData } from '@/data/annual-calendar/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Camera, ArrowRight, Moon, Globe, Mountain, Crown, FileText } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import {
  getCalendarYearSeo,
  getRelatedSeasonGuide,
  type CalendarYearFaqItem,
} from '@/utils/calendarYearSeo';
import { summarizeCalendarSeason } from '@/utils/calendarSeasonSummary';
import { AnnualMonthGuide } from '@/components/annual-calendar/AnnualMonthGuide';

// ─── Pre-computed data imports ──────────────────────────────────────────────

import data2025 from '@/data/annual-calendar/2025.json';
import data2026 from '@/data/annual-calendar/2026.json';
import data2027 from '@/data/annual-calendar/2027.json';
import data2028 from '@/data/annual-calendar/2028.json';
import data2029 from '@/data/annual-calendar/2029.json';
import data2030 from '@/data/annual-calendar/2030.json';

const DATA_BY_YEAR: Record<number, AnnualYearData> = {
  2025: data2025 as AnnualYearData,
  2026: data2026 as AnnualYearData,
  2027: data2027 as AnnualYearData,
  2028: data2028 as AnnualYearData,
  2029: data2029 as AnnualYearData,
  2030: data2030 as AnnualYearData,
};

const CANONICAL_ORIGIN = 'https://www.milkywayplanner.com';

function SeasonGuideLink({ href, label }: { href: string; label: string }) {
  const className = 'text-gold hover:text-gold-light';
  if (href.startsWith('#')) {
    return <a href={href} className={className}>{label}</a>;
  }
  return <Link to={href} className={className}>{label}</Link>;
}

// ─── Structured data ────────────────────────────────────────────────────────

function buildFaqStructuredData(faqItems: CalendarYearFaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

function buildDatasetStructuredData(year: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: `Milky Way Visibility Calendar ${year}`,
    description: `Weekly Milky Way visibility ratings for 30+ dark sky locations worldwide throughout ${year}, based on galactic core position and moon phase calculations.`,
    url: `${CANONICAL_ORIGIN}/milky-way-calendar/${year}`,
    isAccessibleForFree: true,
    temporalCoverage: `${year}-01-01/${year}-12-31`,
    spatialCoverage: 'Global',
    creator: {
      '@type': 'Person',
      name: 'Eric D. Brown',
    },
    variableMeasured: 'Milky Way visibility rating',
  };
}

function buildBreadcrumbStructuredData(year: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${CANONICAL_ORIGIN}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: `Milky Way Calendar ${year}`,
        item: `${CANONICAL_ORIGIN}/milky-way-calendar/${year}`,
      },
    ],
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

const MilkyWayCalendarAnnual = () => {
  const { isSubscribed } = useAuth();
  const { year: yearParam } = useParams<{ year: string }>();
  const year = parseInt(yearParam || '', 10);
  const navigate = useNavigate();
  const yearData = DATA_BY_YEAR[year];
  if (!yearData) {
    return <Navigate to="/milky-way-calendar" replace />;
  }

  const yearSeo = getCalendarYearSeo(year);
  const seasonSummary = year >= 2027 ? summarizeCalendarSeason(yearData) : null;
  const faqItems = seasonSummary
    ? [{ question: seasonSummary.faqQuestion, answer: seasonSummary.faqAnswer }, ...yearSeo.faqItems]
    : yearSeo.faqItems;
  const faqStructuredData = buildFaqStructuredData(faqItems);
  const datasetStructuredData = buildDatasetStructuredData(year);
  const breadcrumbStructuredData = buildBreadcrumbStructuredData(year);
  const relatedSeasonGuide = getRelatedSeasonGuide(year);
  const nextYear = SUPPORTED_YEARS.find((candidate) => candidate > year);
  const [openingBeforePlanner, openingAfterPlanner] = yearSeo.openingParagraphs[1].split('free planner');

  return (
    <>
      <SEOHead
        title={yearSeo.title}
        description={yearSeo.description}
        keywords={yearSeo.keywords}
        canonicalUrl={`/milky-way-calendar/${year}`}
        structuredData={[faqStructuredData, datasetStructuredData, breadcrumbStructuredData]}
      />

      {/* Hero */}
      <section className="bg-slate-950 py-16 sm:py-20 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <span className="eyebrow text-gold-light mb-4 block">{year} Annual Guide</span>
          <h1 className="text-3xl sm:text-4xl md:text-[3.25rem] font-bold text-white tracking-[-0.04em] leading-[1.1] mb-5">
            {yearSeo.h1}
          </h1>
          <div className="text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl space-y-3 mb-3">
            <p>{yearSeo.openingParagraphs[0]}</p>
            <p>
              {openingBeforePlanner}
              <Link to="/" className="text-gold hover:text-gold-light">free planner</Link>
              {openingAfterPlanner}
            </p>
            <p>{yearSeo.openingParagraphs[2]}</p>
            {relatedSeasonGuide && (
              <p>
                {relatedSeasonGuide.href.startsWith('#') ? (
                  <>
                    When is the season on this grid? See{' '}
                    <SeasonGuideLink href={relatedSeasonGuide.href} label={relatedSeasonGuide.label} />
                    .
                  </>
                ) : (
                  <>
                    Need month windows and new-moon dates instead of Saturday ratings? Read the{' '}
                    <SeasonGuideLink href={relatedSeasonGuide.href} label={relatedSeasonGuide.label} />
                    .
                  </>
                )}
              </p>
            )}
            {nextYear != null && (
              <p className="text-base sm:text-lg text-white">
                Planning next season? The{' '}
                <Link
                  to={`/milky-way-calendar/${nextYear}`}
                  className="text-gold hover:text-gold-light font-medium"
                >
                  {nextYear} calendar
                </Link>{' '}
                is up.
              </p>
            )}
          </div>
          <p className="text-sm text-white/50">
            Data computed from astronomical algorithms · Updated for {year}
          </p>
          {/* Year navigation */}
          <div className="flex items-center gap-3 mt-6">
            {SUPPORTED_YEARS.map((y) => (
              <Link
                key={y}
                to={`/milky-way-calendar/${y}`}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  y === year
                    ? 'bg-gold text-slate-950 font-semibold'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                {y}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {seasonSummary && (
        <section className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pt-10">
          <h2 id="season" className="text-xl sm:text-2xl font-semibold text-foreground tracking-[-0.02em] mb-3">
            When is Milky Way season {year}?
          </h2>
          <div className="max-w-3xl space-y-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>{seasonSummary.readingGuide}</p>
            {seasonSummary.locations.map((line) => (
              <p key={line.name}>{line.text}</p>
            ))}
            {seasonSummary.bestSaturdays.length > 0 && (
              <>
                <h3 className="text-base font-semibold text-foreground pt-2">{seasonSummary.bestHeading}</h3>
                <p>{seasonSummary.bestIntro}</p>
                <ul className="list-disc pl-5 space-y-1">
                  {seasonSummary.bestSaturdays.map((saturday) => (
                    <li key={saturday.label}>{saturday.text}</li>
                  ))}
                </ul>
              </>
            )}
            <p>
              <a href="#grid" className="text-gold hover:text-gold-light">The grid</a>
              {' '}on this page is the full Saturday view, one row per location.
              For exact nights at your own location, use the{' '}
              <Link to="/planner" className="text-gold hover:text-gold-light">free planner</Link>.
            </p>
          </div>
        </section>
      )}

      {/* Planner cross-link */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 pt-8">
        <Link
          to="/planner"
          onClick={() => window.scrollTo(0, 0)}
          className="flex items-center justify-between p-4 rounded-[4px] bg-muted/50 hover:bg-muted transition-colors group border border-border/50"
        >
          <div className="flex items-center gap-3">
            <Camera className="w-5 h-5 text-gold" />
            <div>
              <span className="text-foreground font-medium text-sm">Interactive Planner</span>
              <p className="text-muted-foreground text-xs">Get detailed nightly data for any location with the free planning tool</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
        </Link>
      </div>

      {/* Heatmap */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
        <h2 id="grid" className="text-xl sm:text-2xl font-semibold text-foreground tracking-[-0.02em] mb-2">
          {year} Visibility at a Glance
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Each column is a Saturday night. Green = excellent conditions, amber = fair, grey = not visible.
          Click a location name to see its detailed calendar. Or <Link to="/planner" onClick={() => window.scrollTo(0, 0)} className="text-gold hover:text-gold-light">enter custom coordinates</Link>.
        </p>

        <AnnualHeatmapLegend />

        <div className="mt-6">
          <AnnualHeatmapTable
            locations={yearData.locations}
            saturdays={yearData.saturdays}
            onLocationClick={(id) => {
              const loc = yearData.locations.find((l) => l.id === id);
              if (loc) {
                navigate(`/planner?name=${encodeURIComponent(loc.name)}&lat=${loc.latitude}&lon=${loc.longitude}`);
              } else {
                navigate('/planner');
              }
            }}
          />
        </div>
      </section>

      {/* Primary CTAs — locations + premium */}
      <section className="bg-slate-950 py-14 sm:py-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-[-0.02em] mb-3 text-center">
            Ready to Plan Your Shoot?
          </h2>
          <p className="text-white/60 text-center mb-8 max-w-xl mx-auto">
            {isSubscribed
              ? 'Get nightly detail for your exact location with your Premium account.'
              : 'Get nightly detail for your exact location — or unlock the full toolkit with Premium.'}
          </p>
          <div className={`grid gap-6 ${isSubscribed ? '' : 'sm:grid-cols-2'}`}>
            {/* Free planner CTA */}
            <div className={`p-6 sm:p-8 rounded-xl bg-white/5 border border-white/10 ${isSubscribed ? 'max-w-lg mx-auto' : ''}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-white">Plan Your Shoot</h3>
              </div>
              <p className="text-sm text-white/70 leading-relaxed mb-5">
                Enter any coordinates and generate a detailed monthly calendar with nightly
                visibility ratings, galactic core rise/set times, and moon phase data — free.
              </p>
              <Link
                to="/planner"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 w-full justify-center px-5 py-3 border border-gold text-gold hover:bg-gold hover:text-slate-950 font-semibold rounded-lg transition-colors text-sm"
              >
                <Camera className="w-4 h-4" />
                Open Planner
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Premium CTA — hidden for subscribers */}
            {!isSubscribed && (
              <div className="p-6 sm:p-8 rounded-xl bg-gold/10 border border-gold/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Go Premium</h3>
                    <span className="text-xs text-gold">Most popular</span>
                  </div>
                </div>
                <ul className="text-sm text-white/70 space-y-2.5 mb-5">
                  <li className="flex items-start gap-2.5">
                    <FileText className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                    Downloadable PDF calendars for any location
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Globe className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                    Unlimited calendar creation — plan every trip
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Moon className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                    Moon planner with golden hour & blue hour overlays
                  </li>
                </ul>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 w-full justify-center px-5 py-3 bg-gold hover:bg-gold-light text-slate-950 font-semibold rounded-lg transition-colors text-sm"
                >
                  <Crown className="w-4 h-4" />
                  Start Premium Account
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          {/* Workshops CTA — full width below the grid */}
          <div className="mt-6 p-6 sm:p-8 rounded-xl bg-white/5 border border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
                    <Camera className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Learn from the Pros</h3>
                </div>
                <p className="text-sm text-white/70 leading-relaxed max-w-lg">
                  Browse astrophotography workshops at dark sky locations worldwide. Hands-on instruction from experienced photographers.
                </p>
              </div>
              <div className="flex gap-3 flex-shrink-0">
                <Link
                  to="/workshops"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-gold text-gold hover:bg-gold hover:text-slate-950 font-semibold rounded-lg transition-colors text-sm"
                >
                  <Camera className="w-4 h-4" />
                  Browse Workshops
                </Link>
                <Link
                  to="/workshops/instructors"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-semibold rounded-lg transition-colors text-sm"
                >
                  Instructors
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnnualMonthGuide
        year={year}
        data={yearData}
        heading={yearSeo.monthSectionH2}
      />

      {/* Understanding the Data */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
        <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-[-0.02em] mb-6">
          How We Calculate Visibility Ratings
        </h2>
        <div className="grid gap-6 sm:grid-cols-3 mb-8">
          <div className="p-5 rounded-lg bg-secondary/20 border border-border/50">
            <Globe className="w-5 h-5 text-gold-light mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-2">Galactic Core Position</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We compute the galactic center's altitude using Local Sidereal Time and spherical
              trigonometry for each location. The core must be above the horizon during astronomical
              darkness (sun more than 18° below horizon).
            </p>
          </div>
          <div className="p-5 rounded-lg bg-secondary/20 border border-border/50">
            <Moon className="w-5 h-5 text-gold-light mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-2">Moon Illumination</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Even when the core is visible, moonlight washes out detail. Our 0–10 rating scale
              weighs moon illumination heavily: under 5% scores 9–10, while anything
              above 85% drops to 0 regardless of core visibility.
            </p>
          </div>
          <div className="p-5 rounded-lg bg-secondary/20 border border-border/50">
            <Mountain className="w-5 h-5 text-gold-light mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-2">Latitude Matters</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The galactic center sits at declination -29°, so it reaches higher elevations from
              southern latitudes. Locations near 25°S (Atacama, Namib) see the core nearly
              overhead — producing the brightest, most detailed views.
            </p>
          </div>
        </div>
        <p className="text-base text-muted-foreground leading-relaxed">
          Want nightly detail instead of a weekly overview? Our{' '}
          <Link to="/planner" className="text-gold hover:text-gold-light font-medium">
            interactive calendar tool
          </Link>{' '}
          gives month-by-month ratings, galactic core rise and set times, and moon phase for any location.
          The PDF button in the planner is Premium. A shared calendar page has a Download PDF button,
          and that one works for anyone with the link.
        </p>
      </section>

      {/* FAQ */}
      <section className="section-alt py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground tracking-[-0.02em] mb-6">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`faq-${index}`} className="border-border">
                <AccordionTrigger className="text-left text-foreground text-[0.9375rem] hover:no-underline hover:text-gold py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 py-16 sm:py-20">
        <div className="max-w-2xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-[-0.03em] mb-4">
            Get Your Personalized Calendar
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            This overview covers 30+ locations at a weekly level. The free planner adds nightly ratings,
            core timing, and moon phase for your coordinates. It does not include a PDF. The planner PDF
            is Premium, and a shared calendar page can be downloaded by anyone with the link.
          </p>
          <Link
            to="/planner"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-light text-slate-950 font-semibold rounded-lg transition-colors"
          >
            <Camera className="w-5 h-5" />
            Create Your Calendar
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

    </>
  );
};

export default MilkyWayCalendarAnnual;
